import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Unstable_Grid2";
import * as React from "react";

import { useTranslation } from "_metronic/i18n/language";
import { useThemeApp } from "_metronic/materialUIThemeProvider/ThemeProvider";
import { getTestIdsByCategory } from "app/constants/getTestIdsByType";
import Loading from "app/pages/home/components/Loading";
import { AnanseApiService } from "app/services/Ananse/AnanseApiService";
import store from "app/store/store";
import { SimpleSelectPage } from "components";
import { ISelectSearchQuiz } from "interfaces";
import Chart from "react-apexcharts";
import { useRTL } from "themes/RTLProvider/RTLProvider";
import "styles/dashboard-theme.css";

const DashBoard: React.FC = () => {
  const [loading, setLoading] = React.useState(false);

  const translate = useTranslation();
  const isRtl = useRTL();
  const valueTransformIfRtl = isRtl ? "scaleX(-1)" : undefined;
  const { auth } = store.getState();
  const { isDarkMode } = useThemeApp();

  const [expanded, setExpanded] = React.useState<string | false>("panel1");
  const [quizSelected, setQuizSelected] = React.useState(
    {} as ISelectSearchQuiz,
  );
  const [quizChartSelected, setQuizChartSelected] = React.useState({} as any);

  const api = new AnanseApiService();

  const handleChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getCharts = async (quizId: number) => {
    setLoading(true);
    try {
      if (quizId) {
        const response = await api.makeHttpRequest({
          method: "GET",
          url: `dashboard/report?QuizId=${quizId}&UserId=${auth.user.id}`,
        });
        console.log("Response from getCharts:", response);
        setQuizChartSelected(response);
      }
    } catch (error) {
      console.error("Erro ao carregar gráficos: ${error}");
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentage = React.useCallback(() => {
    const percentages: Array<any> = [];
    const questions = quizSelected?.obj?.questions?.length ?? 20;
    const reportGraf = quizChartSelected?.reportGraf ?? [];
    console.log('reportGraf:', reportGraf);
    for (let i = 0; i < questions; i++) {
      const report = reportGraf.find((item: any) => item.acertos === i + 1);
      const qtdTestes = report ? report.qtdTestes : 0;
      const porcent = (
        (qtdTestes / quizChartSelected?.totalTestesRealizados) *
        100
      ).toFixed(2);
      percentages.push(parseFloat(porcent));
    }

    return percentages;
  }, [quizSelected, quizChartSelected]);

  const quizChartData = React.useCallback(() => {
    const result = calculatePercentage();
    const questions = quizSelected?.obj?.questions?.length ?? 20;
    const series: Array<any> = result ?? [];
    const seriesAcertos: Array<any> = [];
    if (questions) {
      for (let i = 1; i <= questions; i++) {
        seriesAcertos.push(i);
      }
    }
    return {
      series,
      seriesAcertos,
    };
  }, [quizSelected, quizChartSelected]);

  const defaultOptions = {
    tooltip: {
      followCursor: true,
      style: {
        fontSize: "14px",
      },
    },
    states: {
      hover: {
        filter: {
          type: "lighten",
          value: 0.1,
        },
      },
    },
  };

  const options: any = {
    ...defaultOptions,
    chart: {
      id: "chart2",
      animations: {
        enabled: true,
        speed: 800,
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      background: "transparent", // Fundo transparente para se adaptar ao tema
    },
    theme: {
      mode: isDarkMode ? "dark" : "light", // Aplicar tema do ApexCharts
    },
    plotOptions: {
      bar: {
        columnWidth: "95%",
        dataLabels: {
          position: "center",
        },
      },
    },
    series: [
      {
        name: translate("screenApp.results.details.percentageHits"),
        type: "column",
        data: quizChartData()?.series,
      },
    ],
    xaxis: {
      categories: quizChartData()?.seriesAcertos,
      labels: {
        style: {
          colors: isDarkMode ? "#E0E0E0" : "#333333", // Cor dos labels baseada no tema
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function(val: any) {
        return val + "%";
      },
      offsetY: 0,
      style: {
        fontSize: "10px",
        colors: ["#ffffff"],
      },
    },
    yaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: false,
        formatter: function(val: any) {
          return val + "%";
        },
      },
    },
    title: {
      text: quizSelected.label,
      floating: true,
      offsetY: 0,
      align: "center",
      style: {
        color: isDarkMode ? "#E0E0E0" : "#333333", // Cor do título baseada no tema
        marginBottom: 12,
      },
    },
    grid: {
      borderColor: isDarkMode ? "#333333" : "#DDDDDD", // Cor da grade baseada no tema
    },
    colors: [isDarkMode ? "#90caf9" : "#1A73E8"], // Cor das barras baseada no tema
  };

  const replaceDecimal = (value: any, decimal = 2) => {
    const text = value?.toFixed(decimal);
    return parseFloat(text) ?? "0";
  };

  const data = [
    {
      title: translate("screenApp.results.details.average"),
      value: quizChartSelected?.media,
    },
    {
      title: translate("screenApp.results.details.standardDeviation"),
      value: replaceDecimal(quizChartSelected?.desvioPadrao, 5),
    },
    {
      title: translate("screenApp.results.details.median"),
      value: quizChartSelected?.mediana,
    },
    {
      title: translate("screenApp.results.details.minimum"),
      value: quizChartSelected?.minAcertos,
    },
    {
      title: translate("screenApp.results.details.maximum"),
      value: quizChartSelected?.maxAcertos,
    },
    {
      title: translate("screenApp.results.details.percentile") + " 10%",
      value: quizChartSelected?.percentil10,
    },
    {
      title: translate("screenApp.results.details.percentile") + " 5%",
      value: quizChartSelected?.percentil5,
    },
    {
      title: translate("test.numbers"),
      value: quizChartSelected?.totalTestesRealizados,
    },
  ];

  console.log("User ID:", auth.user.id);

  const ChartBar = () => {
    return (
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
        sx={{
          backgroundColor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
          color: isDarkMode ? "#E0E0E0" : "#333333",
          '& .MuiAccordionSummary-root': {
            backgroundColor: isDarkMode ? "#333333" : "#F4F4F4",
          },
          '& .MuiAccordionDetails-root': {
            backgroundColor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: isDarkMode ? "#E0E0E0" : "#333333" }} />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              color: isDarkMode ? "#E0E0E0" : "#333333",
              fontWeight: 600,
            }}
          >
            {translate("dashboard_label_one")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Exemplo de uso do SimpleSelectPage para endpoints sem paginação */}

          <SimpleSelectPage
            label={translate("screens.quiz.graphic.identification")}
            endPoint={`/quiz/quizuser/${auth.user.id}`}
            value={quizSelected.id}
            handleChange={(e: any) => {
              const value = e;
              setQuizSelected(value);
              getCharts(value.id);
            }}
            convertObject={(obj: any) => {
              const types = getTestIdsByCategory('olfactory-identifications');
              // Converte obj.type para string para fazer a comparação correta
              const objectType = String(obj.type);

              // Filtra apenas os objetos que possuem o tipo correto
              if (types.indexOf(objectType) === -1) {
                return null;
              }

              // Garante que sempre retorna um objeto válido com label
              return {
                id: obj.quizId,
                value: obj.quizId,
                label: obj.name || obj.title || `Quiz ${obj.quizId}`,
                obj,
              };
            }}
          />

          {quizSelected.id ? (
            <Grid container spacing={2} marginTop={2}>
              <Grid xs={3}>
                <Paper
                  className="stats-table-container"
                  elevation={isDarkMode ? 0 : 1}
                  sx={{
                    backgroundColor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
                    border: isDarkMode ? "1px solid #333333" : "1px solid #DDDDDD",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableCell
                        sx={{
                          backgroundColor: isDarkMode ? "#333333" : "#F4F4F4",
                          color: isDarkMode ? "#E0E0E0" : "#333333",
                          fontWeight: 600,
                          borderBottom: `2px solid ${isDarkMode ? "#333333" : "#DDDDDD"}`,
                        }}
                      >
                        {translate("screenApp.results.details.totalHits")}
                      </TableCell>
                    </TableHead>
                    <TableBody>
                      {data.map(item => (
                        <TableRow
                          key={item.value}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            "&:nth-of-type(even)": {
                              backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
                            },
                            "&:hover": {
                              backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                            },
                          }}
                        >
                          <TableCell
                            align="left"
                            sx={{
                              color: isDarkMode ? "#B0B0B0" : "#666666",
                              borderBottom: `1px solid ${isDarkMode ? "#333333" : "#DDDDDD"}`,
                              fontWeight: 500,
                            }}
                          >
                            {item.title}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              color: isDarkMode ? "#E0E0E0" : "#333333",
                              borderBottom: `1px solid ${isDarkMode ? "#333333" : "#DDDDDD"}`,
                              fontWeight: 600,
                            }}
                          >
                            {item.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              <Grid xs={9} sx={{ transform: valueTransformIfRtl }}>
                <Paper
                  className="chart-container"
                  elevation={isDarkMode ? 0 : 1}
                  sx={{
                    backgroundColor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
                    border: isDarkMode ? "1px solid #333333" : "1px solid #DDDDDD",
                    borderRadius: 2,
                    padding: 2,
                  }}
                >
                  <Chart
                    options={options}
                    type="bar"
                    series={options.series}
                    height={500}
                  />
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Typography
              variant="h5"
              component="h4"
              align="center"
              marginTop={2}
              sx={{
                color: isDarkMode ? "#B0B0B0" : "#666666",
                fontStyle: "italic",
              }}
            >
              {translate("screens.quiz.graphic2")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box
      // className={`dashboard-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
      sx={{
        flexGrow: 1,
        marginBottom: 5,
        // backgroundColor: isDarkMode ? "#121212" : "#FFFFFF",
        minHeight: "100vh",
        padding: 2,
        transition: "background-color 0.3s ease",
      }}
    >
      <Loading isLoading={loading} />
      <ChartBar />
    </Box>
  );
};

export default DashBoard;
